// src/pages/dashboard/library/Chapter.jsx
import React, { Component, createRef } from "react";
import { withRouter } from "../navigation/withNavigation";
import { AppContext } from "../../context/AppContext";
import { getAuthHeaders, urlMap } from "../../utils";

import TitleBar from "./chapterPage/TitleBar";
import Container from "./chapterPage/Container";
import ClipboardNote from "./chapterPage/ClipboardNote";
import ActionButton from "../components/ActionButton";
import StepResultButtons from "./chapterPage/StepResultButtons";
import ShareChapterModal from "../draft/ShareChapterModal";import ChapterHistoryPanel from './chapterPage/ChapterHistoryPanel';import { Icon } from "../components/Icon";
import ToggleSwitch from "../components/ToggleSwitch";
import { withNotifications } from "../../state/notifications/NotificationContext";
import { createPortal } from "react-dom";

class Chapter extends Component {
  static contextType = AppContext;

  getOfflineModeStorageKey = (chapterId) => `chapter.offlineMode.${chapterId}`;
  getOfflineDraftStorageKey = (chapterId) => `chapter.offlineDraft.${chapterId}`;

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: null,
      chapter: null,
      title: "",
      saving: false,
      lastSavedAt: null,
      sidePanel: null, // null | 'coach' | 'agent' | 'summary' | 'history' | 'reflections' | 'tasks' | 'step:*'
      selectedToolsMenuItem: null,
      toolsMenuOpen: false,
      searchEditorOpen: false,
      searchDraft: '',
      prevChapterId: null,
      nextChapterId: null,
      shareModalOpen: false,
      reflectSignal: 0,
      headerAutosave: true,
      headerPomodoroCompletedCount: 0,
      headerExportingPdf: false,
      offlineMode: false,
      offlineSyncing: false,
      committingToHistory: false,
      lastCommittedAt: null,
    };
    this.editorRef = createRef();
    this.clipboardNoteRef = createRef();
    this.toolsMenuRef = createRef();
    this.searchInputRef = createRef();
  }

  componentWillUnmount() {
    if (this._handleOutsideClick) {
      document.removeEventListener('mousedown', this._handleOutsideClick);
    }
    window.removeEventListener('online', this.handleOnlineSync);
    this.context?.clearWriterMode?.();
  }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.params?.chapterId;
    const currId = this.props.params?.chapterId;
    if (currId && currId !== prevId) {
      this.setState({
        loading: true,
        error: null,
        chapter: null,
        title: "",
        saving: false,
        lastSavedAt: null,
        searchEditorOpen: false,
        searchDraft: '',
        prevChapterId: null,
        nextChapterId: null,
        offlineMode: this.getOfflineModeForChapter(currId),
        offlineSyncing: false,
      });
      this.fetchChapter(currId);
    }

    if (!prevProps.location || prevProps.location.search !== this.props.location?.search) {
      if (!this.state.searchEditorOpen) {
        const currentQuery = String(new URLSearchParams(this.props.location?.search || '').get('query') || '').trim();
        if (this.state.searchDraft !== currentQuery) {
          this.setState({ searchDraft: currentQuery });
        }
      }
    }
  }

  componentDidMount() {
    const { chapterId } = this.props.params || {};
    if (chapterId) {
      this.setState({ offlineMode: this.getOfflineModeForChapter(chapterId) });
    }
    if (chapterId) this.fetchChapter(chapterId);
    window.addEventListener('online', this.handleOnlineSync);

    this._handleOutsideClick = (e) => {
      const clickedOutsideTools = this.toolsMenuRef.current && !this.toolsMenuRef.current.contains(e.target);
      if (clickedOutsideTools && (this.state.toolsMenuOpen || this.state.selectedToolsMenuItem)) {
        this.setState({ toolsMenuOpen: false, selectedToolsMenuItem: null });
      }
    };
    document.addEventListener('mousedown', this._handleOutsideClick);

    // Check if URL contains #coach / #agent / #tasks / #summary and set sidePanel accordingly
    if (window.location.hash === "#coach") {
      this.setState({ sidePanel: 'coach' });
    } else if (window.location.hash === "#agent") {
      this.setState({ sidePanel: 'agent' });
    } else if (window.location.hash === "#tasks") {
      this.setState({ sidePanel: 'tasks' });
    } else if (window.location.hash === "#summary") {
      this.setState({ sidePanel: 'summary' });
    } else if (window.location.hash === "#history") {
      this.setState({ sidePanel: 'history' });
    } else if (window.location.hash === "#reflections") {
      this.setState({ sidePanel: 'reflections' });
    }

    if (this.context?.state?.isWriterMode) {
      this.setState({ sidePanel: 'tasks' });
      if (window.location.hash !== '#tasks') {
        window.location.hash = '#tasks';
      }
    }
  }

  getOfflineModeForChapter = (chapterId) => {
    if (!chapterId) return false;
    try {
      return localStorage.getItem(this.getOfflineModeStorageKey(chapterId)) === '1';
    } catch {
      return false;
    }
  };

  setOfflineModeForChapter = (chapterId, enabled) => {
    if (!chapterId) return;
    try {
      localStorage.setItem(this.getOfflineModeStorageKey(chapterId), enabled ? '1' : '0');
    } catch {
      // non-fatal
    }
  };

  saveOfflineDraft = (chapterId, draft) => {
    if (!chapterId) return;
    try {
      localStorage.setItem(this.getOfflineDraftStorageKey(chapterId), JSON.stringify({
        chapterId,
        title: draft?.title || '',
        content: draft?.content || '',
        pendingSync: !!draft?.pendingSync,
        savedAt: draft?.savedAt || new Date().toISOString(),
      }));
    } catch {
      // non-fatal
    }
  };

  getOfflineDraft = (chapterId) => {
    if (!chapterId) return null;
    try {
      const raw = localStorage.getItem(this.getOfflineDraftStorageKey(chapterId));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return {
        chapterId,
        title: String(parsed.title || ''),
        content: String(parsed.content || ''),
        pendingSync: !!parsed.pendingSync,
        savedAt: String(parsed.savedAt || ''),
      };
    } catch {
      return null;
    }
  };

  clearOfflineDraft = (chapterId) => {
    if (!chapterId) return;
    try {
      localStorage.removeItem(this.getOfflineDraftStorageKey(chapterId));
    } catch {
      // non-fatal
    }
  };

  pushChapterUpdate = async (chapterId, title, content) => {
    const res = await fetch(urlMap.updateChapter(chapterId), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ title, content }),
    });
    if (!res.ok) {
      let detail = '';
      try {
        const payload = await res.json();
        detail = payload?.error || payload?.message || '';
      } catch {
        // ignore parse failure
      }
      throw new Error(detail || `HTTP ${res.status}`);
    }
    return res.json();
  };

  syncOfflineDraft = async (chapterId, { silent = false } = {}) => {
    if (!chapterId || this.state.offlineSyncing || !navigator.onLine) return false;
    const draft = this.getOfflineDraft(chapterId);
    if (!draft?.pendingSync) return false;

    this.setState({ offlineSyncing: true });
    try {
      await this.pushChapterUpdate(chapterId, draft.title, draft.content);
      this.clearOfflineDraft(chapterId);
      const ts = new Date().toISOString();
      this.setState({ lastSavedAt: ts, offlineSyncing: false, error: null });
      if (!silent) {
        this.props.notificationsApi?.add?.('Offline chapter changes synced', 'now');
      }
      return true;
    } catch (err) {
      this.setState({ offlineSyncing: false });
      if (!silent) {
        this.props.notificationsApi?.add?.(
          `Sync failed. Your local chapter draft is still safe.${err?.message ? ` (${err.message})` : ''}`,
          'now',
        );
      }
      return false;
    }
  };

  handleOnlineSync = async () => {
    const chapterId = this.props.params?.chapterId;
    if (!chapterId) return;
    await this.syncOfflineDraft(chapterId, { silent: false });
  };

  async fetchChapter(chapterId) {
    try {
      const res = await fetch(urlMap.getChapter(chapterId), {
        method: "GET",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const chapter = await res.json();

      const localDraft = this.getOfflineDraft(chapterId);
      const localTs = localDraft?.savedAt ? Date.parse(localDraft.savedAt) : 0;
      const remoteTs = chapter?.updated_at ? Date.parse(chapter.updated_at) : 0;
      const preferLocal = !!localDraft && (localDraft.pendingSync || (localTs && remoteTs && localTs > remoteTs));

      this.setState({
        chapter: preferLocal ? { ...chapter, title: localDraft.title, content: localDraft.content } : chapter,
        title: preferLocal ? localDraft.title : (chapter?.title || ""),
        loading: false,
        error: null,
      });

      if (localDraft?.pendingSync && navigator.onLine) {
        void this.syncOfflineDraft(chapterId, { silent: true });
      }

      // Fetch sibling chapters for prev/next navigation
      const { projectId } = this.props.params || {};
      if (projectId) {
        this.fetchSiblingChapters(projectId, chapter);
      }
    } catch (err) {
      const localDraft = this.getOfflineDraft(chapterId);
      if (localDraft) {
        this.setState({
          chapter: { id: chapterId, title: localDraft.title, content: localDraft.content },
          title: localDraft.title,
          loading: false,
          error: null,
        });
        return;
      }
      this.setState({ error: err.message || "Failed to load", loading: false });
    }
  }

  async fetchSiblingChapters(projectId, currentChapter) {
    try {
      const res = await fetch(urlMap.listChapters(projectId), {
        method: "GET",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      if (!res.ok) return;
      const data = await res.json();
      const chapters = (Array.isArray(data) ? data : data.chapters || [])
        .filter((c) => !c.archived_at)
        .sort((a, b) => (a.chapter_number ?? a.order_no ?? 0) - (b.chapter_number ?? b.order_no ?? 0));
      const idx = chapters.findIndex((c) => c.id === currentChapter.id);
      this.setState({
        prevChapterId: idx > 0 ? chapters[idx - 1].id : null,
        nextChapterId: idx < chapters.length - 1 ? chapters[idx + 1].id : null,
      });
    } catch (_) {
      // non-critical, ignore
    }
  }

  // Expose plain text to Analysis
  getPlainText = () => this.editorRef.current?.getPlainText?.() || "";

  handleGoBack = () => {
    const { projectId } = this.props.params || {};
    if (projectId) this.props.navigate(`/project/${projectId}`);
    else this.props.navigate("/home");
  };

  handlePrevChapter = () => {
    const { prevChapterId } = this.state;
    const { projectId } = this.props.params || {};
    if (prevChapterId) this.props.navigate(`/project/${projectId}/chapter/${prevChapterId}`);
  };

  handleNextChapter = () => {
    const { nextChapterId } = this.state;
    const { projectId } = this.props.params || {};
    if (nextChapterId) this.props.navigate(`/project/${projectId}/chapter/${nextChapterId}`);
  };

  handleTitleChange = (title) => this.setState({ title });

  // Editor delegates saves here
  handleSave = async ({ chapterId, title, content }) => {
    this.setState({ saving: true });
    const timestamp = new Date().toISOString();

    if (this.state.offlineMode) {
      this.saveOfflineDraft(chapterId, { title, content, pendingSync: true, savedAt: timestamp });
      this.setState((prev) => ({
        saving: false,
        lastSavedAt: timestamp,
        chapter: prev.chapter ? { ...prev.chapter, title, content } : prev.chapter,
      }));
      if (navigator.onLine) {
        void this.syncOfflineDraft(chapterId, { silent: true });
      }
      return;
    }

    try {
      await this.pushChapterUpdate(chapterId, title, content);
      this.clearOfflineDraft(chapterId);
      const ts = new Date().toISOString();
      this.setState({ lastSavedAt: ts, saving: false });
    } catch (err) {
      this.saveOfflineDraft(chapterId, { title, content, pendingSync: true, savedAt: timestamp });
      console.error("Save failed", err);
      this.props.notificationsApi?.add?.(
        `Save failed. Your chapter draft was stored locally and will sync when online.${err?.message ? ` (${err.message})` : ''}`,
        'now'
      );
      this.setState({ saving: false });
      throw err;
    }
  };

  handleCommitToHistory = async () => {
    const { chapter, committingToHistory } = this.state;
    if (!chapter?.id || committingToHistory) return;
    this.setState({ committingToHistory: true });
    try {
      await fetch(urlMap.commitChapter(chapter.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      this.setState({ committingToHistory: false, lastCommittedAt: new Date().toISOString() });
    } catch {
      this.setState({ committingToHistory: false });
    }
  };

  handleOfflineModeToggle = async (enabled) => {
    const chapterId = this.props.params?.chapterId;
    if (!chapterId) return;

    this.setOfflineModeForChapter(chapterId, !!enabled);
    this.setState({ offlineMode: !!enabled });

    if (enabled) {
      this.props.notificationsApi?.add?.('Offline mode enabled for this chapter', 'now');
      return;
    }

    this.props.notificationsApi?.add?.('Offline mode disabled. Attempting sync…', 'now');
    await this.syncOfflineDraft(chapterId, { silent: false });
  };

  toggleCoach = () => {
    this.setState(
      (s) => {
        const newPanel = s.sidePanel === 'coach' ? null : 'coach';
        window.location.hash = newPanel ? `#${newPanel}` : '';
        return { sidePanel: newPanel };
      }
    );
  };

  toggleAgent = () => {
    this.setState(
      (s) => {
        const newPanel = s.sidePanel === 'agent' ? null : 'agent';
        window.location.hash = newPanel ? `#${newPanel}` : '';
        return { sidePanel: newPanel };
      }
    );
  };

  toggleSummary = () => {
    this.setState((s) => {
      const newPanel = s.sidePanel === 'summary' ? null : 'summary';
      window.location.hash = newPanel ? '#summary' : '';
      return { sidePanel: newPanel };
    });
  };

  toggleHistory = () => {
    this.setState((s) => {
      const newPanel = s.sidePanel === 'history' ? null : 'history';
      window.location.hash = newPanel ? '#history' : '';
      return { sidePanel: newPanel };
    });
  };

  toggleReflections = () => {
    this.setState((s) => {
      const newPanel = s.sidePanel === 'reflections' ? null : 'reflections';
      window.location.hash = newPanel ? '#reflections' : '';
      return { sidePanel: newPanel };
    });
  };

  selectToolsMenuItem = (itemKey, action) => {
    this.setState({ selectedToolsMenuItem: itemKey, toolsMenuOpen: false }, () => {
      action?.();
    });
  };

  handleToolsTriggerClick = () => {
    this.setState((prev) => ({ toolsMenuOpen: !prev.toolsMenuOpen, selectedToolsMenuItem: null }));
  };

  setChapterSearchQuery = (rawQuery) => {
    const query = String(rawQuery || '').trim();
    const currentPath = this.props.location?.pathname
      || `/project/${this.props.params?.projectId}/chapter/${this.props.params?.chapterId}`;
    const params = new URLSearchParams(this.props.location?.search || '');

    if (query.length >= 2) params.set('query', query);
    else params.delete('query');

    const nextSearch = params.toString();
    const nextPath = `${currentPath}${nextSearch ? `?${nextSearch}` : ''}`;
    this.props.navigate(nextPath, { replace: true });
  };

  handleChapterSearch = () => {
    this.openChapterSearchEditor();
  };

  editChapterSearchQuery = () => {
    const currentQuery = String(new URLSearchParams(this.props.location?.search || '').get('query') || '').trim();
    this.setState({ searchEditorOpen: true, searchDraft: currentQuery }, () => {
      this.searchInputRef.current?.focus();
      this.searchInputRef.current?.select?.();
    });
  };

  openChapterSearchEditor = () => {
    const currentQuery = String(new URLSearchParams(this.props.location?.search || '').get('query') || '').trim();
    this.setState({ searchEditorOpen: true, searchDraft: currentQuery }, () => {
      this.searchInputRef.current?.focus();
      this.searchInputRef.current?.select?.();
    });
  };

  applyChapterSearchDraft = () => {
    this.setChapterSearchQuery(this.state.searchDraft);
    this.setState({ searchEditorOpen: false });
  };

  cancelChapterSearchEditor = () => {
    const currentQuery = String(new URLSearchParams(this.props.location?.search || '').get('query') || '').trim();
    this.setState({ searchEditorOpen: false, searchDraft: currentQuery });
  };

  onSearchDraftChange = (e) => {
    this.setState({ searchDraft: e.target.value });
  };

  onSearchDraftKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.applyChapterSearchDraft();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      this.cancelChapterSearchEditor();
    }
  };

  focusNextChapterSearchHit = () => {
    this.editorRef.current?.focusNextSearchHit?.();
  };

  clearChapterSearchQuery = () => {
    this.setChapterSearchQuery('');
    this.setState({ searchDraft: '', searchEditorOpen: false });
  };

  toggleStepPanel = (stepKey) => {
    
    if (stepKey === 'DetectChapterHolesStep') {
      const isOpening = this.state.sidePanel !== 'tasks';
      const applyTasksState = () => {
        this.setState((s) => {
          const newPanel = s.sidePanel === 'tasks' ? null : 'tasks';
          window.location.hash = newPanel ? '#tasks' : '';
          return { sidePanel: newPanel };
        });
      };
      if (isOpening && this.state.chapter?.id) {
        this.fetchChapter(this.state.chapter.id).finally(applyTasksState);
      } else {
        applyTasksState();
      }
      return;
    }

    const target = `step:${stepKey}`;
    const isOpening = this.state.sidePanel !== target;

    const applyPanelState = () => {
      this.setState((s) => {
        const newPanel = s.sidePanel === target ? null : target;
        window.location.hash = newPanel ? '#results' : '';
        return { sidePanel: newPanel };
      });
    };

    applyPanelState();
  };

  handleWriterModeChange = (isWriterMode) => {
    if (isWriterMode) {
      this.setState({ sidePanel: 'tasks' });
      window.location.hash = '#tasks';
    }
    this.context?.setWriterMode?.(isWriterMode);
  };

  toggleShareModal = () => {
    this.setState((prev) => ({
      shareModalOpen: !prev.shareModalOpen,
      selectedToolsMenuItem: null,
    }));
  };

  closeShareModal = () => {
    this.setState({ shareModalOpen: false });
  };

  handleHeaderControlsChange = ({ autosave, pomodoroCompletedCount, exportingPdf } = {}) => {
    this.setState((prev) => {
      const nextAutosave = typeof autosave === 'boolean' ? autosave : prev.headerAutosave;
      const nextPomodoroCount = Number.isFinite(Number(pomodoroCompletedCount))
        ? Number(pomodoroCompletedCount)
        : prev.headerPomodoroCompletedCount;
      const nextExportingPdf = typeof exportingPdf === 'boolean' ? exportingPdf : prev.headerExportingPdf;

      if (
        prev.headerAutosave === nextAutosave &&
        prev.headerPomodoroCompletedCount === nextPomodoroCount &&
        prev.headerExportingPdf === nextExportingPdf
      ) {
        return null;
      }

      return {
        headerAutosave: nextAutosave,
        headerPomodoroCompletedCount: nextPomodoroCount,
        headerExportingPdf: nextExportingPdf,
      };
    });
  };

  handleHeaderAutosaveToggle = (enabled) => {
    this.editorRef.current?.setAutosave?.(enabled);
    this.setState({ headerAutosave: !!enabled });
  };

  exportPdfFromHeader = () => {
    this.editorRef.current?.onExportPdf?.();
  };

  render() {
    const { loading, error, chapter, title, saving, lastSavedAt, sidePanel, prevChapterId, nextChapterId, selectedToolsMenuItem, toolsMenuOpen, searchEditorOpen, searchDraft, headerAutosave, headerPomodoroCompletedCount, headerExportingPdf, offlineMode, offlineSyncing, committingToHistory, lastCommittedAt } = this.state;
    const searchQuery = new URLSearchParams(this.props.location?.search || '').get('query') || '';
    const trimmedSearchQuery = String(searchQuery || '').trim();
    const hasSearchQuery = trimmedSearchQuery.length >= 2;
    const isWriterMode = !!this.context?.state?.isWriterMode;
    const showCoach = sidePanel === 'coach';
    const chapterIssues = chapter?.chapterIssues || [];
    const tasksCount = chapterIssues.filter((i) => i.status === 'in_progress').length;
    const chapterTitleSlot = typeof document !== 'undefined'
      ? document.getElementById('topbar-chapter-title-slot')
      : null;

    if (loading) return <div className="home"><h2>Loading chapter…</h2></div>;
    if (error)   return <div className="home"><h2>Error</h2><div>{error}</div></div>;
    if (!chapter) return null;

    return (
      <div className={`home chapter-home chapter-page-root ${sidePanel ? "coach-open" : ""} ${isWriterMode ? "writer-mode" : ""}`}>
        {chapterTitleSlot && createPortal(
          <TitleBar
            value={title}
            onChange={this.handleTitleChange}
            chapterNumber={chapter?.chapter_number ?? chapter?.order_no}
          />,
          chapterTitleSlot,
        )}

        {!isWriterMode && (
          <>
            <div className="chapter-back-nav">
              <ActionButton
                onClick={this.handleGoBack}
                className="btn subtle pf-back"
                ariaLabel="Back to Chapters"
              >
                ← Back to Chapters
              </ActionButton>
              <div className="chapter-nav-divider" />
              <ActionButton
                onClick={this.handlePrevChapter}
                className="btn subtle chapter-nav-btn"
                ariaLabel="Previous chapter"
                disabled={!prevChapterId}
              >
                ‹ Previous
              </ActionButton>
              <ActionButton
                onClick={this.handleNextChapter}
                className="btn subtle chapter-nav-btn"
                ariaLabel="Next chapter"
                disabled={!nextChapterId}
              >
                Next ›
              </ActionButton>
            </div>

            <div className="chapters-header chapter-header-layout library-header">
              <div className="library-header-title-group">
                <div className="chapter-info-group">
                  <div className="project-meta-steps-row">
                    {searchEditorOpen ? (
                      <div className="chapter-search-editor" role="search">
                        <input
                          ref={this.searchInputRef}
                          type="text"
                          className="chapter-search-editor-input"
                          placeholder="Find in this chapter"
                          value={searchDraft}
                          onChange={this.onSearchDraftChange}
                          onKeyDown={this.onSearchDraftKeyDown}
                          aria-label="Find in this chapter"
                        />
                        <button
                          type="button"
                          className="chapter-search-editor-apply"
                          onClick={this.applyChapterSearchDraft}
                          title="Apply search"
                          aria-label="Apply search"
                        >
                          Apply
                        </button>
                        <button
                          type="button"
                          className="chapter-search-editor-cancel"
                          onClick={this.cancelChapterSearchEditor}
                          title="Cancel"
                          aria-label="Cancel"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : hasSearchQuery ? (
                      <div className="chapter-search-chip" role="status" aria-live="polite">
                        <button
                          type="button"
                          className="chapter-search-chip-main"
                          onClick={this.focusNextChapterSearchHit}
                          title="Jump to next match"
                          aria-label="Jump to next match"
                        >
                          <span className="chapter-search-chip-icon" aria-hidden>
                            <Icon.SearchLoop />
                          </span>
                          <span className="chapter-search-chip-label">Search</span>
                          <span className="chapter-search-chip-term">{trimmedSearchQuery}</span>
                        </button>
                        <button
                          type="button"
                          className="chapter-search-chip-edit"
                          onClick={this.editChapterSearchQuery}
                          title="Edit search term"
                          aria-label="Edit search term"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="chapter-search-chip-clear"
                          onClick={this.clearChapterSearchQuery}
                          title="Clear search"
                          aria-label="Clear search"
                        >
                          ×
                        </button>
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className={`scenes-avatar-btn chapter-modal-launch-btn${sidePanel === 'summary' ? ' is-active' : ''}`}
                      onClick={this.toggleSummary}
                      title={sidePanel === 'summary' ? 'Hide chapter summary' : 'View chapter summary'}
                      aria-label={sidePanel === 'summary' ? 'Hide chapter summary' : 'View chapter summary'}
                    >
                      <span className="scenes-avatar-icon"><Icon.StoryBible /></span>
                      <span className="scenes-avatar-hint">Summary</span>
                    </button>
                    <StepResultButtons
                      onStepClick={this.toggleStepPanel}
                      activeStep={
                        sidePanel?.startsWith('step:') ? sidePanel.slice(5) :
                        sidePanel === 'tasks' ? 'DetectChapterHolesStep' : null
                      }
                      tasksCount={tasksCount}
                    />
                    <button
                      type="button"
                      className={`scenes-avatar-btn chapter-modal-launch-btn${sidePanel === 'history' ? ' is-active' : ''}`}
                      onClick={this.toggleHistory}
                      title={sidePanel === 'history' ? 'Hide writing history' : 'View writing history'}
                      aria-label={sidePanel === 'history' ? 'Hide writing history' : 'View writing history'}
                    >
                      <span className="scenes-avatar-icon" aria-hidden>
                        <svg width="16" height="16" viewBox="0 0 24 24" focusable="false">
                          <path d="M3 3h8v8H3zm0 10h8v8H3zm10-10h8v8h-8zm0 10h8v8h-8z" fill="currentColor" />
                        </svg>
                      </span>
                      <span className="scenes-avatar-hint">History</span>
                    </button>
                    <div className="chapter-tasks-controls" role="group" aria-label="Tasks bar controls">
                      <button
                        type="button"
                        className={`scenes-avatar-btn chapter-modal-launch-btn${sidePanel === 'reflections' ? ' is-active' : ''}`}
                        onClick={this.toggleReflections}
                        title={sidePanel === 'reflections' ? 'Hide reflections panel' : 'View reflections panel'}
                        aria-label={headerPomodoroCompletedCount > 0
                          ? `Pomodoro sprints for this chapter: ${headerPomodoroCompletedCount}. ${sidePanel === 'reflections' ? 'Hide' : 'View'} reflections panel.`
                          : `${sidePanel === 'reflections' ? 'Hide' : 'View'} reflections panel.`}
                      >
                        <span className="scenes-avatar-icon"><Icon.Reflection /></span>
                        <span className="scenes-avatar-hint">Reflections</span>
                      </button>
                      <ToggleSwitch
                        id="chapter-offline-mode"
                        name="chapter-offline-mode"
                        checked={offlineMode}
                        onChange={(e) => this.handleOfflineModeToggle(e.target.checked)}
                        label={offlineSyncing ? 'Syncing…' : 'Offline mode'}
                        className="chapter-autosave-toggle"
                      />
                      <ToggleSwitch
                        id="chapter-autosave"
                        name="chapter-autosave"
                        checked={headerAutosave}
                        onChange={(e) => this.handleHeaderAutosaveToggle(e.target.checked)}
                        label="Auto save"
                        className="chapter-autosave-toggle"
                      />
                    </div>
                    <ClipboardNote ref={this.clipboardNoteRef} projectId={this.props.params?.projectId} hideTrigger />
                    <button
                      type="button"
                      className="scenes-avatar-btn chapter-inline-icon-btn"
                      aria-label={headerExportingPdf ? 'Exporting PDF' : 'Export PDF'}
                      title={headerExportingPdf ? 'Exporting PDF...' : 'Export PDF'}
                      onClick={this.exportPdfFromHeader}
                      disabled={headerExportingPdf}
                    >
                      <span className="scenes-avatar-icon" aria-hidden>
                        <svg width="16" height="16" viewBox="0 0 24 24" focusable="false">
                          <path d="M12 3a1 1 0 0 1 1 1v8.59l2.3-2.3a1 1 0 1 1 1.4 1.42l-4 3.98a1 1 0 0 1-1.4 0l-4-3.98a1 1 0 1 1 1.4-1.42l2.3 2.3V4a1 1 0 0 1 1-1z" fill="currentColor" />
                          <path d="M5 14a1 1 0 0 1 1 1v3h12v-3a1 1 0 1 1 2 0v4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1z" fill="currentColor" />
                        </svg>
                      </span>
                      <span className="scenes-avatar-hint">Export</span>
                    </button>
                    <button
                      type="button"
                      className="scenes-avatar-btn chapter-inline-icon-btn"
                      aria-label="Share chapter"
                      title="Share chapter"
                      onClick={this.toggleShareModal}
                    >
                      <span className="scenes-avatar-icon"><Icon.Share width="16" height="16" /></span>
                      <span className="scenes-avatar-hint">Share</span>
                    </button>
                    <div
                      ref={this.toolsMenuRef}
                      className={`project-tools-menu chapter-tools-menu chapter-tools-menu--inline${toolsMenuOpen ? ' project-tools-menu--open' : ''}${selectedToolsMenuItem ? ' project-tools-menu--selection-locked' : ''}`}
                      aria-label="Tools menu"
                    >
                      <button
                        type="button"
                        className={`scenes-avatar-btn chapter-inline-icon-btn project-tools-trigger chapter-tools-trigger${toolsMenuOpen ? ' is-open' : ''}${selectedToolsMenuItem ? ' has-selection' : ''}`}
                        aria-label="Tools"
                        title="Tools"
                        aria-expanded={toolsMenuOpen}
                        onClick={this.handleToolsTriggerClick}
                      >
                        <span className="scenes-avatar-icon"><Icon.Tools /></span>
                        <span className="scenes-avatar-hint">Tools</span>
                      </button>
                      <div className="project-tools-popover chapter-tools-popover" role="menu" aria-label="Chapter tools">
                        <button
                          type="button"
                          className={`project-tools-item chapter-tools-item${selectedToolsMenuItem === 'clipboard' ? ' is-selected' : ''}`}
                          role="menuitem"
                          title="Open clipboard"
                          onClick={() => this.selectToolsMenuItem('clipboard', () => this.clipboardNoteRef.current?.toggle?.())}
                        >
                          <Icon.Clipboard />
                          <span>Clipboard</span>
                        </button>
                        <button
                          type="button"
                          className={`project-tools-item chapter-tools-item${sidePanel === 'coach' ? ' is-active' : ''}${selectedToolsMenuItem === 'coach' ? ' is-selected' : ''}`}
                          role="menuitem"
                          title={sidePanel === 'coach' ? 'Hide coach' : 'Show coach'}
                          onClick={() => this.selectToolsMenuItem('coach', this.toggleCoach)}
                        >
                          <Icon.Coach />
                          <span>Coach</span>
                        </button>
                        <button
                          type="button"
                          className={`project-tools-item chapter-tools-item${sidePanel === 'agent' ? ' is-active' : ''}${selectedToolsMenuItem === 'agent' ? ' is-selected' : ''}`}
                          role="menuitem"
                          title={sidePanel === 'agent' ? 'Hide agent' : 'Show agent'}
                          onClick={() => this.selectToolsMenuItem('agent', this.toggleAgent)}
                        >
                          <Icon.Agent />
                          <span>Agent</span>
                        </button>
                        <button
                          type="button"
                          className={`project-tools-item chapter-tools-item${selectedToolsMenuItem === 'search' ? ' is-selected' : ''}`}
                          role="menuitem"
                          title="Find in chapter"
                          onClick={() => this.selectToolsMenuItem('search', this.handleChapterSearch)}
                        >
                          <Icon.SearchLoop />
                          <span>Search</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <Container
          ref={this.editorRef}
          chapter={chapter}
          title={title}
          searchQuery={searchQuery}
          saving={saving}
          lastSavedAt={lastSavedAt}
          lastCommittedAt={lastCommittedAt}
          committingToHistory={committingToHistory}
          onCommitToHistory={this.handleCommitToHistory}
          isWriterMode={isWriterMode}
          onSave={this.handleSave}          
          showCoach={showCoach}
          sidePanel={sidePanel}
          onToggleCoach={this.toggleCoach}
          onToggleAgent={this.toggleAgent}
          onClosePanel={() => this.setState({ sidePanel: null })}
          onWriterModeChange={this.handleWriterModeChange}
          projectId={this.props.params?.projectId}
          projectIssues={chapter.chapterIssues || []}
          reflectSignal={this.state.reflectSignal}
          onReflectionConsumed={() => this.setState({ reflectSignal: 0 })}
          onHeaderControlsChange={this.handleHeaderControlsChange}
        />

        <ShareChapterModal
          isOpen={this.state.shareModalOpen}
          onClose={this.closeShareModal}
          projectId={this.props.params?.projectId}
          projectTitle={chapter?.project?.title || chapter?.projectTitle || ''}
          chapterId={chapter?.id}
          chapterTitle={chapter?.title}
          chapterIndex={chapter?.chapter_number ?? chapter?.order_no ?? 0}
          userId={this.context?.state?.userId}
        />
      </div>
    );
  }
}

export default withRouter(withNotifications(Chapter));
